import './PowerConsumptionPage.css';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';
import {AppDispatch} from '../../redux/store';
import RootState from '../../redux/states/root.state';
import {fetchSpa} from '../../redux/actions/spa.actions';
import {fetchSpaHistory} from '../../redux/actions/spaHistory.actions';
import {clearSpaHistory} from '../../redux/reducers/spaHistory.reducer';
import Header from '../../components/common/Header/Header';
import NotFound from '../General/NotFound';
import AccessDenied from '../General/AccessDenied';
import Spinner from '../../components/common/Spinner';
import useRoleCheck from '../../hooks/useRoleCheck';
import useMQTT from '../../hooks/useMQTT';
import { isV1Spa } from '../../hooks/useSpaCommand';
import {AlertIcon, ChevronRightIcon} from '../../components/common/Icons/Icons';
import {DATE_RANGE_OPTIONS, TIMEZONE_OPTIONS, POWER_FIELDS, getOffsetHours, formatTimezoneLabel} from './constants';
import {PowerDelta, sumDeltas} from './powerData';
import PowerRing from './components/PowerRing';
import CostBreakdown from './components/CostBreakdown';
import {SpaSettingsLabels} from '../../mqttData/settings/spaSettingsLabels';

const HOUR_MS = 60 * 60 * 1000;

const PowerConsumptionPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const {id} = useParams();
  const {canAccess: isCustomer} = useRoleCheck(['customer']);

  const {data: spa, loading: spaLoading, error} = useSelector((state: RootState) => state.spa);
  const {data: settingsData} = useSelector((state: RootState) => state.settings);
  const {data: historyData, loading: historyLoading, partialLoading: historyPartialLoading} = useSelector((state: RootState) => state.spaHistory);

  // V1 / Coldfire spas never report to AWS IoT or DynamoDB, so there is no power history to show.
  const isV1 = !!id && !!spa?.id && spa.id.toLowerCase() === id.toLowerCase() && isV1Spa(spa);
  useMQTT(isV1 ? '' : (id ?? ''));

  const [rangeValue, setRangeValue] = useState<string>('last_7_days');
  const [tzValue, setTzValue] = useState<string>('Etc/UTC');
  const [tzInitialized, setTzInitialized] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchSpa(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (tzInitialized) return;
    const spaTz = settingsData?.timezone;
    if (spaTz) {
      const direct = TIMEZONE_OPTIONS.find(t => t.value === spaTz);
      if (direct) {
        setTzValue(direct.value);
        setTzInitialized(true);
        return;
      }
      const spaOffset = getOffsetHours(spaTz);
      const sameOffset = TIMEZONE_OPTIONS.find(t => getOffsetHours(t.value) === spaOffset);
      if (sameOffset) {
        setTzValue(sameOffset.value);
        setTzInitialized(true);
      }
    }
  }, [settingsData, tzInitialized]);

  const selectedRange = DATE_RANGE_OPTIONS.find(r => r.value === rangeValue) ?? DATE_RANGE_OPTIONS[1];
  const selectedTz = TIMEZONE_OPTIONS.find(t => t.value === tzValue) ?? TIMEZONE_OPTIONS.find(t => t.value === 'Etc/UTC')!;
  const selectedTzHours = getOffsetHours(selectedTz.value);

  useEffect(() => {
    if (!spa?.id || isV1) return;
    const dateTo = Date.now();
    const dateFrom = dateTo - selectedRange.ms;
    dispatch(clearSpaHistory());
    dispatch(fetchSpaHistory({
      guid: spa.id,
      date_from: dateFrom,
      date_to: dateTo,
      keys: POWER_FIELDS.map(f => f.id),
      type: 'live',
    }));
  }, [dispatch, spa?.id, selectedRange.ms, isV1]);

  const deltas: PowerDelta[] = useMemo(() => {
    return (historyData ?? []).map((row: any) => {
      const values: Record<string, number> = {};
      POWER_FIELDS.forEach(f => {
        values[f.id] = typeof row[f.id] === 'number' ? row[f.id] : 0;
      });
      return {
        timestamp: typeof row.timestamp === 'number' ? row.timestamp : new Date(row.timestamp).getTime(),
        values,
      };
    });
  }, [historyData]);

  const totals = useMemo(() => sumDeltas(deltas), [deltas]);
  const numDays = Math.max(1, Math.round(selectedRange.ms / (24 * HOUR_MS)));

  const peakSettings = useMemo(() => {
    if (!settingsData) return null;
    const keys = ['Mode1','Mode2','Mode3','Mode4','EndMode1','EndMode2','EndMode3','EndMode4'];
    const out: Record<string, number> = {};
    let allPresent = true;
    keys.forEach(k => {
      const labelKey = (SpaSettingsLabels as any)[k] ?? k;
      const v = settingsData[labelKey] ?? settingsData[k];
      if (v === undefined || v === null) allPresent = false;
      else out[k] = v;
    });
    return allPresent ? out : null;
  }, [settingsData]);

  if (error) return <NotFound />;
  if (spaLoading || !spa?.id) return <Spinner />;

  const spaLink = isCustomer ? `/spa-control/${spa?.id}` : `/spa/${spa?.id}`;
  const loadingHistory = historyLoading || historyPartialLoading;

  if (isV1) {
    return (
      <div className="page pwr-page">
        <Header />
        <main className="pwr-main">
          <section className="pwr-usage">
            <p>Power consumption history is not available for V1 spas.</p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="page pwr-page">
      <Header />
      <main className="pwr-main">
        <div className="pwr-layout">
          {/* Left: usage ring */}
          <section className="pwr-usage">
            <div className="pwr-usage-head">
              <a
                className="pwr-spa-link"
                href={spaLink}
                onClick={e => { e.preventDefault(); navigate(spaLink); }}
              >
                {spa?.nick_name ?? 'n/a'}
                <ChevronRightIcon/>
              </a>
              {historyPartialLoading && <span className="spinner pwr-loading-dot" role="progressbar" aria-label="Loading more data"/>}
            </div>

            {/* Range */}
            <div className="segmented pwr-range" role="group" aria-label="Date range">
              {DATE_RANGE_OPTIONS.map(r => (
                <button
                  key={r.value}
                  className={rangeValue === r.value ? 'active' : ''}
                  aria-pressed={rangeValue === r.value}
                  onClick={() => setRangeValue(r.value)}
                >
                  {r.label.replace('Last ', '')}
                </button>
              ))}
            </div>

            <PowerRing totalsCentiKwh={totals} loading={loadingHistory}/>
          </section>

          {/* Right: timezone, cost */}
          <section className="pwr-side">
            {/* Up front, before anyone reads the numbers */}
            <p className="pwr-caution" role="note">
              <AlertIcon/>
              <span>This feature is for information purposes only, the accuracy of information is highly dependant on the strength of the internet connection.</span>
            </p>

            <label className="field">
              <span className="field-label">Timezone</span>
              <select className="input" value={tzValue} onChange={e => setTzValue(e.target.value)}>
                {TIMEZONE_OPTIONS.map(t => (
                  <option key={t.value} value={t.value}>{formatTimezoneLabel(t)}</option>
                ))}
              </select>
            </label>

            <CostBreakdown
              deltas={deltas}
              totalsCentiKwh={totals}
              numDays={numDays}
              tzHours={selectedTzHours}
              peakSettings={peakSettings}
            />

          </section>
        </div>
      </main>
    </div>
  );
};

export default PowerConsumptionPage;
